import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { addShortCode, fetchShortCode, updateShortCode } from 'api/short-codes';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useInfiniteCampaigns } from 'customHooks/useInfiniteCampaigns';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { SmsKeyword } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface ShortCodeFormValues {
    model: string;
    keyword: string;
}

const ShortCodeFormDrawer = () => {
    const { success, error } = useToast();
    const { accountId } = useCurrentAccount();
    const { goBack } = useDialogRoute();
    const { keywordId } = useParams<{ keywordId: string }>();

    const { data } = useSuspenseQuery({
        queryKey: accountQueryKeys.shortCode(accountId, keywordId),
        queryFn: () =>
            conditionallyFetchOrNull(
                () => fetchShortCode(accountId, parseInt(keywordId)),
                !!keywordId,
            ),
    });
    const campaign = data?.model;

    const { data: campaignData, ref } = useInfiniteCampaigns();
    const campaigns = campaignData?.pages;

    const defaultValues = {
        model: campaign?.id.toString() ?? '',
        keyword: data?.keyword || '',
    };
    const methods = Form.useGivebutterForm<ShortCodeFormValues>({
        defaultValues,
        mode: 'onChange',
    });
    const {
        control,
        formState: { isDirty, isValid },
        path,
        handleSubmit,
        setError,
        setValue,
        resetFormButton,
        watch,
    } = methods;

    const model = watch('model');
    const campaignValue = useMemo(() => {
        return model === campaign?.id.toString()
            ? campaign.title
            : campaigns?.find(c => c.id.toString() === model)?.title;
    }, [model, campaign, campaigns]);

    const { mutate: submit, isPending: isSubmitting } = useMutation<
        SmsKeyword,
        GivebutterAxiosError,
        ShortCodeFormValues
    >({
        mutationFn: ({ keyword, model }) => {
            return data
                ? updateShortCode(accountId, data.id, { keyword, modelId: model ?? '' })
                : addShortCode(accountId, { keyword, modelId: model ?? '' });
        },
        onSuccess: () => {
            success(`Keyword ${data ? 'updated' : 'created'} successfully!`);
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.shortCodes(accountId),
            });
            goBack();
        },
        onError: e => {
            error(e.response?.data.message || 'Something went wrong. Please try again.');
            const errors = e.response?.data.errors;

            if (errors) {
                for (const field in errors) {
                    setError(field as keyof ShortCodeFormValues, {
                        type: 'server',
                        message: errors[field][0],
                    });
                }
            }
            resetFormButton();
        },
    });

    return (
        <Form.GivebutterFormProvider {...methods}>
            <Drawer.Header>
                <Drawer.Title>{keywordId ? 'Update' : 'Add'} Keyword</Drawer.Title>
            </Drawer.Header>
            <form className="contents" onSubmit={handleSubmit(values => submit(values))}>
                <Drawer.Body className="flex flex-col gap-4">
                    <Form.Container>
                        <Form.Label name={path('model')}>Campaign</Form.Label>
                        <Form.SingleSelect
                            control={control}
                            name="model"
                            rules={{ required: 'Please select a campaign.' }}
                        >
                            <SingleSelect.Trigger aria-label="Campaign">
                                <SingleSelect.Value placeholder="Select a campaign">
                                    {campaignValue}
                                </SingleSelect.Value>
                            </SingleSelect.Trigger>
                            <SingleSelect.Content>
                                {campaigns?.map(camp => {
                                    return (
                                        <SingleSelect.Item value={camp.id.toString()} key={camp.id}>
                                            <SingleSelect.ItemText>
                                                {camp.title}
                                            </SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                    );
                                })}
                                <div ref={ref} className="h-4" />
                            </SingleSelect.Content>
                        </Form.SingleSelect>
                    </Form.Container>
                    <Form.Container>
                        <Form.Label name={path('keyword')}>
                            Keyword <Form.LabelAsterisk />
                        </Form.Label>
                        <p className="text-xs">
                            Make this short and memorable. No spaces or special characters allowed.
                        </p>
                        <Form.TextInputRoot
                            control={control}
                            name="keyword"
                            rules={{
                                required: 'Please enter a Keyword',
                                pattern: {
                                    value: /^[a-zA-Z0-9]+$/,
                                    message: 'Keyword may only contain letters and numbers.',
                                },
                            }}
                        >
                            <Form.TextInputForm
                                placeholder="Ex: DANCE"
                                onChange={e =>
                                    setValue('keyword', e.target.value.toUpperCase(), {
                                        shouldDirty: true,
                                        shouldTouch: true,
                                        shouldValidate: true,
                                    })
                                }
                            />
                        </Form.TextInputRoot>
                        <Form.Error name={path('keyword')} />
                    </Form.Container>
                </Drawer.Body>
                <Drawer.Footer>
                    <div className="flex w-full flex-wrap-reverse items-center justify-between gap-2">
                        <Drawer.Cancel asChild={true}>
                            <Button variant="outline" color="neutral">
                                Cancel
                            </Button>
                        </Drawer.Cancel>

                        <Form.SubmitButton
                            disabled={isSubmitting || !isDirty || !isValid}
                            isSubmitting={isSubmitting}
                        >
                            {data ? 'Update Keyword' : 'Add Keyword'}
                        </Form.SubmitButton>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.GivebutterFormProvider>
    );
};

export { ShortCodeFormDrawer };
